import { ActionTree } from 'vuex';
import { ISentEmail } from '../types/sent-email';
import { IDocumentActionsState } from './document-actions.types';
import {
  EDocumentActionsAction,
  EDocumentActionsMutation,
  getDefaultState,
} from './document-actions.constants';
import { RootState } from '@/store/store';
import { IDocument } from '@lib-shared/types/Document';
import { DocumentActionService } from '@/api/document-actions/services/document-action.service';
import { DocumentService } from '@/api/documents/services/document.service';
import { IUser } from '@/types/User';
import { RecipientService } from '@/api/recipients/services/recipient.service';
import bugsnagClient from '@lib-shared/bugsnag';
import { alertNamespace, EAlertAction } from '@/alert/store/alert.constants';
import { translate } from '@/helpers/translation.helper';
import { IRecipient } from '@lib-shared/types/Recipient';
import { IUpdateDocument } from '@/document-edit/assembler/document/types/update-document';
import { IDocumentEventItem } from '@/document-view/types/document-events-item';
import documentViewService from '@/services/document-view.service';
import { IRecallDocumentDTO } from '@/api/document-actions/types/recall-document.dto';

export const actions: ActionTree<IDocumentActionsState, RootState> = {
  [EDocumentActionsAction.Reset]({ commit }) {
    commit(EDocumentActionsMutation.SetState, getDefaultState());
  },
  [EDocumentActionsAction.ResetLoadingState]({ commit }) {
    commit(EDocumentActionsMutation.SetDone, false);
    commit(EDocumentActionsMutation.SetLoading, false);
  },
  [EDocumentActionsAction.Error]({ commit }, error) {
    if (bugsnagClient) {
      bugsnagClient.notify(error);
    }
    commit(EDocumentActionsMutation.SetError, true);
    commit(EDocumentActionsMutation.SetLoading, false);
  },
  async [EDocumentActionsAction.LoadDocument]({ dispatch, commit }, documentId: string) {
    try {
      commit(EDocumentActionsMutation.SetLoadDocumentFailed, false);
      const document: IDocument = await DocumentService.loadDocument(documentId);
      dispatch(EDocumentActionsAction.LoadDocumentSuccess, document);
    } catch (error) {
      dispatch(EDocumentActionsAction.LoadDocumentFailed, error);
    }
  },
  [EDocumentActionsAction.LoadDocumentSuccess]({ commit }, document: IDocument) {
    commit(EDocumentActionsMutation.SetDocument, document);
  },
  [EDocumentActionsAction.LoadDocumentFailed]({ commit }) {
    commit(EDocumentActionsMutation.SetLoadDocumentFailed, true);
  },
  [EDocumentActionsAction.ResetError]({ commit }) {
    commit(EDocumentActionsMutation.SetError, false);
  },
  [EDocumentActionsAction.UpdateExpirationDate](
    { commit, dispatch },
    {
      document,
      expirationDate,
      email,
      emailMessage,
      emailSubject,
    }: {
      document: IDocument;
      expirationDate: string;
      email: boolean;
      emailMessage: string;
      emailSubject: string;
    }
  ) {
    commit(EDocumentActionsMutation.SetLoading, true);
    const updatedDocument: IDocument = { ...document, expirationDate };
    try {
      DocumentActionService.updateExpirationDate({
        expirationDate,
        email,
        emailMessage,
        emailSubject,
        documentId: document.id,
      });
      dispatch(EDocumentActionsAction.UpdateDocumentSuccess, updatedDocument);
    } catch (e) {
      dispatch(EDocumentActionsAction.Error, e);
    }
  },
  async [EDocumentActionsAction.UpdateDocument](
    { commit, dispatch },
    { updatedDocument, document }: { updatedDocument: IUpdateDocument; document: IDocument }
  ) {
    try {
      commit(EDocumentActionsMutation.SetLoading, true);
      await DocumentService.updateDocument(updatedDocument, document.id);
      dispatch(EDocumentActionsAction.UpdateDocumentSuccess, {
        ...document,
        ...updatedDocument,
      });
    } catch (e) {
      dispatch(EDocumentActionsAction.Error, e);
    }
  },
  [EDocumentActionsAction.UpdateDocumentSuccess]({ commit }, updatedDocument: IDocument) {
    commit(EDocumentActionsMutation.SetUpdatedDocument, updatedDocument);
    setTimeout(() => {
      commit(EDocumentActionsMutation.SetDone, true);
    }, 750);
  },
  async [EDocumentActionsAction.SendEmailReminder](
    { commit, dispatch },
    {
      documentId,
      isNotSignedSelected,
      isNotOpenedSelected,
    }: { documentId: string; isNotSignedSelected: boolean; isNotOpenedSelected: boolean }
  ) {
    commit(EDocumentActionsMutation.SetLoading, true);
    try {
      await DocumentActionService.sendEmailReminder({
        documentId,
        isNotSignedSelected,
        isNotOpenedSelected,
      });
      dispatch(EDocumentActionsAction.SendEmailReminderSuccess);
    } catch (e) {
      dispatch(EDocumentActionsAction.Error, e);
    }
  },
  [EDocumentActionsAction.SendEmailReminderSuccess]({ commit }) {
    commit(EDocumentActionsMutation.SetDone, true);
  },
  async [EDocumentActionsAction.SendVideoReminder](
    { commit, dispatch },
    {
      documentId,
      videoId,
      reminderText,
    }: { documentId: string; videoId: string; reminderText: string }
  ) {
    commit(EDocumentActionsMutation.SetLoading, true);
    try {
      await DocumentActionService.sendVideoReminder({
        documentId,
        videoId,
        reminderText,
      });
      dispatch(EDocumentActionsAction.SendVideoReminderSuccess);
    } catch (e) {
      dispatch(EDocumentActionsAction.Error, e);
    }
  },
  [EDocumentActionsAction.SendVideoReminderSuccess]({ commit }) {
    commit(EDocumentActionsMutation.SetDone, true);
    commit(EDocumentActionsMutation.SetLoading, false);
  },
  async [EDocumentActionsAction.Recall]({ dispatch, commit }, payload: IRecallDocumentDTO) {
    commit(EDocumentActionsMutation.SetLoading, true);
    try {
      await DocumentActionService.recall(payload);
      dispatch(EDocumentActionsAction.RecallSuccess);
    } catch (error) {
      dispatch(EDocumentActionsAction.Error, error);
    }
  },
  async [EDocumentActionsAction.RecallSuccess]({ dispatch, commit }) {
    setTimeout(() => {
      commit(EDocumentActionsMutation.SetDone, true);
      dispatch(
        `${alertNamespace}/${EAlertAction.ShowSuccess}`,
        translate('successfully-recalled-document'),
        { root: true }
      );
    }, 750);
  },
  async [EDocumentActionsAction.Delete]({ dispatch, commit }, documentId: string) {
    commit(EDocumentActionsMutation.SetLoading, true);
    try {
      await DocumentService.delete(documentId);
      dispatch(EDocumentActionsAction.DeleteSuccess);
    } catch (error) {
      dispatch(EDocumentActionsAction.Error, error);
    }
  },
  async [EDocumentActionsAction.DeleteSuccess]({ dispatch, commit }) {
    setTimeout(() => {
      commit(EDocumentActionsMutation.SetDone, true);
      dispatch(
        `${alertNamespace}/${EAlertAction.ShowSuccess}`,
        translate('successfully-deleted-document'),
        { root: true }
      );
    }, 750);
  },
  async [EDocumentActionsAction.ChangeOwner](
    { commit, dispatch },
    payload: { newOwner: IUser; document: IDocument }
  ) {
    const { newOwner, document } = payload;
    const documentId: string = document.id;
    const updatedDocument: IDocument = { ...document, user: newOwner };
    try {
      commit(EDocumentActionsMutation.SetLoading, true);
      await DocumentService.updateDocument({ userId: newOwner.id }, documentId);
      dispatch(EDocumentActionsAction.ChangeOwnerSuccess, updatedDocument);
    } catch (error) {
      dispatch(EDocumentActionsAction.Error, error);
    }
  },
  async [EDocumentActionsAction.ChangeOwnerSuccess]({ commit }, updatedDocument: IDocument) {
    commit(EDocumentActionsMutation.SetUpdatedDocument, updatedDocument);
    commit(EDocumentActionsMutation.SetDone, true);
  },
  async [EDocumentActionsAction.CreateNewVersion](
    { dispatch, commit },
    payload: {
      isDocumentContentChecked: boolean;
      isRecipientsChecked: boolean;
      isEmailContentChecked: boolean;
      isRecallChecked: boolean;
      documentId: string;
    }
  ) {
    commit(EDocumentActionsMutation.SetLoading, true);
    try {
      const newDocumentId = await DocumentActionService.createNewVersion(payload);
      dispatch(EDocumentActionsAction.CreateNewVersionSuccess, newDocumentId);
    } catch (error) {
      dispatch(EDocumentActionsAction.Error, error);
    }
  },
  async [EDocumentActionsAction.CreateNewVersionSuccess]({ commit }, documentId) {
    commit(EDocumentActionsMutation.SetNewVersionDocumentId, documentId);
    setTimeout(() => {
      commit(EDocumentActionsMutation.SetDone, true);
    }, 750);
  },
  async [EDocumentActionsAction.UpdateScheduledSendingTime](
    { dispatch, commit },
    { document, scheduledSendingTime }: { document: IDocument; scheduledSendingTime: string }
  ) {
    commit(EDocumentActionsMutation.SetLoading, true);
    try {
      await DocumentActionService.updateSchedule({
        sendingDate: scheduledSendingTime,
        documentId: document.id,
      });
      const updatedDocument = { ...document, scheduledSendingTime };
      dispatch(EDocumentActionsAction.UpdateDocumentSuccess, updatedDocument);
    } catch (e) {
      dispatch(EDocumentActionsAction.Error, e);
    }
  },
  async [EDocumentActionsAction.UpdateContactNumbers](
    { commit, dispatch },
    { document, updatedRecipients }: { document: IDocument; updatedRecipients: IRecipient[] }
  ) {
    commit(EDocumentActionsMutation.SetLoading, true);
    try {
      await Promise.all(
        updatedRecipients.map(async (recipient: IRecipient) => {
          const { id } = recipient;
          await RecipientService.saveRecipient(recipient, document.id);
          await RecipientService.updateRecipient(id, recipient);
        })
      );
      const newRecipients: IRecipient[] = document.recipients.map(
        (oldRecipient: IRecipient) =>
          updatedRecipients.find((recipient: IRecipient) => recipient.id === oldRecipient.id) ||
          oldRecipient
      );
      dispatch(EDocumentActionsAction.UpdateDocumentSuccess, {
        ...document,
        recipients: newRecipients,
      });
    } catch (e) {
      dispatch(EDocumentActionsAction.Error, e);
    }
  },
  async [EDocumentActionsAction.SendSMSReminder](
    { commit, dispatch },
    {
      documentId,
      message,
      isNotSignedSelected,
      isNotOpenedSelected,
    }: {
      documentId: string;
      message: string;
      isNotSignedSelected: boolean;
      isNotOpenedSelected: boolean;
    }
  ) {
    commit(EDocumentActionsMutation.SetLoading, true);
    try {
      await DocumentActionService.sendSMSReminder(
        documentId,
        message,
        isNotSignedSelected,
        isNotOpenedSelected
      );
      dispatch(EDocumentActionsAction.SendSMSReminderSuccess);
    } catch (error) {
      dispatch(EDocumentActionsAction.Error, error);
    }
  },
  [EDocumentActionsAction.SendSMSReminderSuccess]({ commit }) {
    commit(EDocumentActionsMutation.SetDone, true);
  },
  async [EDocumentActionsAction.UploadSigned](
    { commit, dispatch },
    {
      documentId,
      uploadFile,
      withoutVerification,
    }: { documentId: string; uploadFile: File; withoutVerification: boolean }
  ) {
    try {
      commit(EDocumentActionsMutation.SetLoading, true);
      if (!withoutVerification) {
        const uploadFileList: string = await DocumentActionService.saveUploadDocument(uploadFile);
        await DocumentActionService.uploadSigned({ documentId, uploadFileList });
      } else {
        await DocumentActionService.uploadSignedWithoutVerification({ documentId });
      }
      dispatch(EDocumentActionsAction.UploadSignedSuccess);
    } catch (error) {
      dispatch(EDocumentActionsAction.Error, error);
    }
  },

  [EDocumentActionsAction.UploadSignedSuccess]({ commit }) {
    commit(EDocumentActionsMutation.SetDone, true);
    commit(EDocumentActionsMutation.SetLoading, false);
  },

  async [EDocumentActionsAction.CancelScheduledSending](
    { commit, dispatch },
    { documentId }: { documentId: string }
  ) {
    try {
      commit(EDocumentActionsMutation.SetLoading, true);
      await DocumentActionService.cancel(documentId);
      dispatch(EDocumentActionsAction.CancelScheduledSendingSuccess);
    } catch (error) {
      dispatch(EDocumentActionsAction.Error, error);
    }
  },
  async [EDocumentActionsAction.CancelScheduledSendingSuccess]({ commit }) {
    setTimeout(() => {
      commit(EDocumentActionsMutation.SetDone, true);
    }, 750);
  },
  async [EDocumentActionsAction.LoadSentEmail]({ commit, dispatch }, documentId: string) {
    try {
      const email: ISentEmail = await DocumentActionService.sentEmail(documentId);
      commit(EDocumentActionsMutation.SetSentEmail, email);
    } catch (error) {
      if (bugsnagClient) {
        bugsnagClient.notify(error);
      }
      dispatch(
        `${alertNamespace}/${EAlertAction.ShowDanger}`,
        translate('could-not-load-sent-email'),
        { root: true }
      );
    }
  },
  async [EDocumentActionsAction.LoadDocumentEvents]({ commit, dispatch }, documentId: string) {
    try {
      const events: IDocumentEventItem[] = await documentViewService.loadDocumentEvents(documentId);
      commit(EDocumentActionsMutation.SetDocumentEvents, events);
    } catch (error) {
      if (bugsnagClient) {
        bugsnagClient.notify(error);
      }
      dispatch(`${alertNamespace}/${EAlertAction.ShowDanger}`, translate('could-not-load-events'), {
        root: true,
      });
    }
  },
  async [EDocumentActionsAction.SendSMSToRecipient](
    { commit, dispatch },
    {
      documentId,
      recipient,
      message,
    }: { documentId: string; recipient: IRecipient; message: string }
  ) {
    commit(EDocumentActionsMutation.SetLoading, true);
    try {
      await DocumentActionService.sendSMSToRecipient({ documentId, recipient, message });
      dispatch(EDocumentActionsAction.SendSMSToRecipientSuccess);
    } catch (error) {
      dispatch(EDocumentActionsAction.Error, error);
    }
  },
  async [EDocumentActionsAction.SendSMSToRecipientSuccess]({ commit }) {
    commit(EDocumentActionsMutation.SetDone, true);
    commit(EDocumentActionsMutation.SetLoading, false);
  },
  async [EDocumentActionsAction.MarkAsLost]({ commit, dispatch }, documentId: string) {
    commit(EDocumentActionsMutation.SetLoading, true);
    try {
      await DocumentActionService.markAsLost(documentId);
      dispatch(EDocumentActionsAction.MarkAsLostSuccess);
    } catch (error) {
      dispatch(EDocumentActionsAction.Error, error);
    }
  },
  async [EDocumentActionsAction.MarkAsLostSuccess]({ commit }) {
    commit(EDocumentActionsMutation.SetDone, true);
    commit(EDocumentActionsMutation.SetLoading, false);
  },
  async [EDocumentActionsAction.SetDocument]({ commit }, document: IDocument) {
    commit(EDocumentActionsMutation.SetDocument, document);
  },
};
